import useHttp from "@/composables/useHttp"
import {heicTo, isHeic} from "heic-to"
import axios, {type AxiosProgressEvent} from "axios"
import {useQuotaStore} from "@/stores/quota"

export type FileUploadSettings = {
  heicQuality: number,
  onProgress: (event: { progress: number, scanning_for_virus: boolean }) => void
  folderId?: number
  dontRefreshQuota?: boolean
}

const defaultSettings: FileUploadSettings = {
  heicQuality: 0.8,
  onProgress: () => {
  }
}

export default function useFileUpload(settings: Partial<FileUploadSettings>) {
  const settingsWithDefaults: FileUploadSettings = {...defaultSettings, ...settings}
  return async (file: File) => {
    const {$http, $error} = useHttp()

    if (await isHeic(file)) {
      file = new File(
        [
          await heicTo({
            blob: file,
            type: "image/jpeg",
            quality: settingsWithDefaults.heicQuality
          })
        ],
        file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"),
        {type: "image/jpeg"}
      )
    }

    const fileName = file.name
    const fileType = file.type.startsWith('video/') ? 'video/mp4' : file.type

    // Get presigned URL
    let response = await $http.post('/file-uploads', {
      file_name: fileName,
      file_type: fileType,
      folder_id: settingsWithDefaults.folderId,
      file_size: file.size,
    }).catch(e => e)
    if ($error(response.status, response.data.message, response.data.errors, false)) {
      throw new Error(response.data.message)
    }

    const {fileId, url} = response.data

    // Upload file to S3
    // Note: We use axios directly here to handle the file upload so we don't send the apps bearer token
    response = await axios.put(url, file, {
      headers: {
        'Content-Type': fileType,
      },
      onUploadProgress(progressEvent: AxiosProgressEvent) {
        const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? 1))
        settingsWithDefaults.onProgress({
          progress: progress,
          // This is needed because sometimes the event fires after the await returns
          scanning_for_virus: progress === 100
        })
      }
    }).catch(e => e)
    if (response.status !== 200) {
      // The file row already exists, so it would keep counting toward the folder size and quota
      await $http.delete(`/files/${fileId}`).catch(e => e)

      throw new Error('Failed to upload file. Please contact support.')
    }

    settingsWithDefaults.onProgress({
      progress: 100,
      scanning_for_virus: true
    })

    if (import.meta.env.VITE_APP_ENV === 'local') {
      // Mocking the S3 event that will be sent in production
      $http.put(`/mock-virus-scans/${fileId}`).catch(e => e)
    }

    let isScanning = true
    let timeout = 90
    while (isScanning) {
      response = await $http.get(`/file-uploads/${fileId}`).catch(e => e)
      if ($error(response.status, response.data.message, response.data.errors, false)) {
        throw new Error(response.data.message)
      }
      if (response.data.completed) {
        isScanning = false
        if (response.data.result) {
          throw new Error('File contains a virus and cannot be uploaded')
        }
        break
      }

      timeout = timeout - 1
      if (timeout < 0) {
        throw new Error('Timeout waiting for file to be processed')
      }
      // Wait for 1 second before checking again
      await new Promise(resolve => setTimeout(resolve, 1000))
    }

    if (!settings.dontRefreshQuota) {
      await useQuotaStore().refresh()
    }

    // Save file to database
    return response.data.file
  }
}
